import React, { useEffect, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerContainer } from '../../../components/Drawer/DrawerContainer';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { enqueueSnackbar } from 'notistack';
import DatePickerComponent from '../../../components/layout/DatePicker';
import { ActionType } from '../consts/avisoObra.types';

interface DrawerAvisoObraProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    actionType: ActionType | null;
    selectedObra: any;
    onSubmit: (payload: any) => void;
}

const DrawerAvisoObra: React.FC<DrawerAvisoObraProps> = ({
    open,
    setOpen,
    actionType,
    selectedObra,
    onSubmit
}) => {
    const { t } = useTranslation('avisoObra');

    const [fechaSuspension, setFechaSuspension] = useState<Date | null>(null);
    const [fechaReinicio, setFechaReinicio] = useState<Date | null>(null);
    const [fechaExtension, setFechaExtension] = useState<Date | null>(null);

    const handleConfirm = () => {
        if (actionType === 'Suspender') {
            if (!fechaSuspension || !fechaReinicio) {
                enqueueSnackbar(t('drawer.warningCampos'), { variant: 'warning' });
                return;
            }
            onSubmit({
                idObra: selectedObra?.id,
                fechaSuspension,
                fechaReinicio
            });
        }

        if (actionType === 'Extender') {
            if (!fechaExtension) {
                enqueueSnackbar(t('drawer.warningCampos'), { variant: 'warning' });
                return;
            }
            onSubmit({
                idObra: selectedObra?.id,
                fechaExtension
            });
        }

        handleClose();
    };

    const handleClose = () => {
        setFechaSuspension(null);
        setFechaReinicio(null);
        setFechaExtension(null);
        setOpen(false);
    };

    useEffect(() => {
        if (!open) {
            setFechaSuspension(null);
            setFechaReinicio(null);
            setFechaExtension(null);
        }
    }, [open]);

    const renderFields = () => {
        if (actionType === 'Suspender') {
            return (
                <>
                    <Grid item xs={12}>
                        <DatePickerComponent
                            label={t('form.fechaSuspension')}
                            value={fechaSuspension}
                            onChange={setFechaSuspension}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DatePickerComponent
                            label={t('form.fechaReinicio')}
                            value={fechaReinicio}
                            onChange={setFechaReinicio}
                            required
                        />
                    </Grid>
                </>
            );
        }

        if (actionType === 'Extender') {
            return (
                <Grid item xs={12}>
                    <DatePickerComponent
                        label={t('form.fechaExtension')}
                        value={fechaExtension}
                        onChange={setFechaExtension}
                        required
                    />
                </Grid>
            );
        }

        return null;
    };

    return (
        <DrawerContainer open={open} setOpen={handleClose}>
            <DrawerHeader
                onIconClick={handleClose}
                text={t(`drawer.title.${actionType?.toLowerCase()}`)}
            />

            <Box px={3} py={1}>
                <Grid container spacing={2}>
                    {renderFields()}
                </Grid>

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        display: 'flex',
                        gap: 2
                    }}
                >
                    <Button variant="outlined" onClick={handleClose}>
                        {t('form.cancelar')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={
                            (actionType === 'Suspender' && (!fechaSuspension || !fechaReinicio)) ||
                            (actionType === 'Extender' && !fechaExtension)
                        }
                    >
                        {t('form.guardar')}
                    </Button>
                </Box>
            </Box>
        </DrawerContainer>
    );
};

export default DrawerAvisoObra;
